import moment, { Moment } from '@e3dc-react/shell/Libs/moment'
import { ChartView, CustomTrace, RechartData, RechartSum } from '../../chart-kit/shared/utils/chartModel'
import { WallboxHistory } from '../../../Types/responses/wallboxes/{sn}'
import {
  isBypass,
  isBypassNoCharge,
  isConsumption,
  isEnera,
  isProduction,
  PowermeterHistory,
  PowermeterType,
} from '../../../Types/responses/powermeter-values/{sn}'
import { isStartOfDay, isStartOfHour, isStartOfMonth } from './datetime'
import { Breakthrough, ForecastData, ForecastPower, HistoryData, StorageInfoData, SystemInfo } from '../models/systemDataStoreModel'
import { getRoundedNumber, ONE_HOUR_S } from './helperFunctions'
import { traceConf, TraceKey } from '../../system-charts/shared/config/traceConf'
import { binarySearchObject } from './binarySearch'
import { isEmpty } from 'lodash'
import { TFunction } from 'i18next'
import { ThreeSixtySavingsTotalConsumption } from '../../dynamic-power-prices/hooks/use-three-sixty-savings-consumption'
import { addSelfProductionOfLastFifteenMinutes } from './self-production'
import { ChartViewFactors } from '../types'

export type HistoryChartData = {
  ASOC: number
  BatPowerIn: number
  BatPowerOut: number
  DC_PowerL1: number
  DC_PowerL2: number
  DC_PowerL3: number
  DC_PowerSolar: number
  Deration: number //Hausverbrauch + derateAtPower
  ForecastPower: number
  NetIn: number
  NetOut: number
  SelfProduction: number
  Autarky: number
  ts: number
  WeatherBreakthrough: boolean
  DstSwitch: boolean
  ConsumptionTotal: number
  ConsumptionHouse: number
  Wallbox_energyAll?: number
  Wallbox_energySolar?: number
  Wallbox_energyDischarged?: number
  Wallbox_energyNetOut?: number
  Powermeter_production?: number
  Powermeter_consumption?: number
  Temperature?: number
  Greenconsumption?: number
  [key: string]: number | boolean | undefined
}

export interface ForecastChartData {
  'f-ASOC': number
  'f-BatPowerIn': number
  'f-BatPowerOut': number
  'f-DC_PowerSolar': number
  'f-Deration': number
  'f-ConsumptionHouse'?: number
  'f-ConsumptionTotal'?: number
  ForecastPower: number
  'f-NetIn': number
  'f-NetOut': number
  ts: number
  WeatherBreakthrough: boolean
  [key: string]: number | boolean | undefined
  forecast: true
}

export const getSums = (
  historyChartData: HistoryChartData[],
  forecastData: ForecastChartData[],
  forecastPower: ForecastPower[],
  factor: ChartViewFactors,
  threeSixtySavingsTotalConsumption: ThreeSixtySavingsTotalConsumption
): Record<string, RechartSum> => {
  const { historySum, sumKeys } = getHistorySums(historyChartData, factor)
  const forecastSums = getForecastSums(forecastData, factor)
  const notIncludedKeys = ['ts', 'WeatherBreakthrough']

  if (historyChartData.length > 0) {
    /**
     * Add last history item to the forecast sum to have the same values as in PortalV2
     * TODO: This should be reverted one day, because sums display forecast values + history values
     * and this lastHistoryItem is currently in both value arrays and so counted double
     */
    const lastHistoryItem = historyChartData[historyChartData.length - 1]
    const lastHistorySum = getHistorySums([lastHistoryItem], factor)
    Object.keys(forecastSums).forEach(key => (forecastSums[key] += lastHistorySum.historySum[key] ?? 0))
  }

  const forecastPowerSum = forecastPower.reduce((prev, current) => prev + (current.power ?? 0) / factor, 0)

  const resultSum: Record<string, RechartSum> = {}

  /* next day may have no history data so only forecast data and its keys should be used */
  const keysToTake = !isEmpty(historyChartData) ? sumKeys : Object.keys(forecastSums)

  keysToTake
    ?.filter(key => !notIncludedKeys.includes(key))
    .forEach(sumKey => {
      if (sumKey === 'ASOC') {
        resultSum[sumKey] = {
          history: null,
          forecast: null,
        }
      } else if (sumKey === 'Temperature') {
        resultSum[sumKey] = {
          history: null,
          forecast: null,
        }
      } else if (sumKey === 'ForecastPower') {
        resultSum[sumKey] = {
          history: forecastPowerSum,
          forecast: null,
        }
      } else {
        resultSum[sumKey] = {
          history: historySum?.[sumKey] ?? 0,
          forecast: forecastSums?.[sumKey] ?? 0,
        }
      }
    })

  return {
    ...resultSum,
    ...threeSixtySavingsTotalConsumption,
    NetOutWithoutThreeSixtySavings: {
      history: Math.max(0, (resultSum.NetOut?.history ?? 0) - (threeSixtySavingsTotalConsumption?.ThreeSixtySavings?.history ?? 0)),
      forecast: 0,
    },
  }
}

export const getHistorySums = (
  historyChartData: HistoryChartData[],
  factor: number
): { historySum: Record<keyof HistoryChartData, number>; sumKeys: string[] } => {
  const historySum: Record<keyof HistoryChartData, number> = {}
  if (!historyChartData || !historyChartData.length) {
    return {
      historySum: {},
      sumKeys: [],
    }
  }

  const sumKeys = historyChartData.length > 0 ? Object.keys(historyChartData[0]) : []

  sumKeys.forEach(historyChartDataKey => {
    //initialize object with all keys available in historyChartData
    historySum[historyChartDataKey] = 0
  })

  historyChartData.forEach(data => {
    return Object.keys(data).forEach(key => {
      const typedKey = key as TraceKey

      const customTrace = traceConf[typedKey]?.custom
      if (customTrace?.sign === -1) {
        return (historySum[key] -= getRoundedNumber((data[key] as number) / factor))
      } else {
        if (historySum[key] >= 0) {
          return (historySum[key] += getRoundedNumber((data[key] as number) / factor))
        }
      }
    })
  })

  return { historySum, sumKeys }
}

const getForecastSums = (forecastData: ForecastChartData[], factor: number): Record<keyof HistoryChartData, number> => {
  const forecastSum: Record<keyof HistoryChartData, number> = {}
  if (!forecastData || !forecastData.length) return {}

  const sumKeys = Object.keys(forecastData[0])
  sumKeys.forEach(forecastDataKey => {
    const normalizedKey = forecastDataKey.startsWith('f-') ? (forecastDataKey.slice(2) as keyof HistoryChartData) : forecastDataKey
    //initialize object with all keys available in historyData
    forecastSum[normalizedKey] = 0
  })

  forecastData.forEach(data => {
    return Object.keys(data).forEach(key => {
      const normalizedKey = key.startsWith('f-') ? (key.slice(2) as keyof HistoryChartData) : key
      const typedKey = key as keyof ForecastChartData
      const dataValue = data[typedKey] as number

      if (dataValue && dataValue >= 0) {
        return (forecastSum[normalizedKey] += dataValue / factor)
      }
    })
  })
  return forecastSum
}

const getHistoryObjectTimestamp = (accumulatedValues: boolean, previousTime: number, currentTime: number, view?: ChartView): number => {
  if (accumulatedValues) return previousTime
  if (view === 'week') {
    if (isStartOfHour(moment(currentTime))) return currentTime
    /**
     * It is possible that the last timestamp of history values is not a full hour value.
     * We need to update that so we can calculate differences
     */
    const nextHourValue = moment(currentTime).add(1, 'hour').startOf('hour').valueOf()
    return nextHourValue
  }
  return currentTime
}

export const getHistoryChartDataForView = (
  historyData: HistoryData[],
  wallboxChartData: Record<number, WallboxHistory[]>,
  powermeterChartData: Record<string, PowermeterHistory[]>,
  forecastPower: ForecastPower[],
  breakthroughData: Breakthrough[],
  systemInfo: SystemInfo,
  view: ChartView,
  dynamicPowerPricesEnabled: boolean /* this is a temporary condition added with AF-3237. The plan is to remove it after SW1 and SW2 align. */
): HistoryChartData[] => {
  if (!historyData) return []
  const storageInfo = systemInfo?.storage.info
  const filteredHistoryData: HistoryData[] = []
  const foundDataSet = new Set()
  let selfProduction = 0
  for (let i = 0; i < historyData.length; i++) {
    const historyDataItem = historyData[i]
    const yearOfData = moment(historyDataItem.time).year()
    const monthOfData = moment(historyDataItem.time).month()
    const weekOfData = moment(historyDataItem.time).week()
    const dayOfData = moment(historyDataItem.time).date()
    const hourOfData = moment(historyDataItem.time).hour()
    const minuteOfData = moment(historyDataItem.time).minute()

    const yearMonthKey = `${yearOfData}-${monthOfData}`
    const weekDayKey = `${weekOfData}-${dayOfData}`
    const weekDayHourKey = `${weekOfData}-${dayOfData}-${hourOfData}`
    const dayHourMinuteKey = `${dayOfData}-${hourOfData}-${minuteOfData}`

    const currentIndex = view === 'week' ? i + 4 : i
    selfProduction = selfProduction + addSelfProductionOfLastFifteenMinutes(historyData, currentIndex)

    if (i === historyData.length - 1) {
      filteredHistoryData.push({ ...historyDataItem, selfProduction })
    }

    if (view === 'day' && !foundDataSet.has(dayHourMinuteKey)) {
      filteredHistoryData.push({ ...historyDataItem, selfProduction })
      selfProduction = 0
      foundDataSet.add(dayHourMinuteKey)
    }
    if (view === 'week' && !foundDataSet.has(weekDayHourKey)) {
      filteredHistoryData.push({ ...historyDataItem, selfProduction })
      selfProduction = 0
      foundDataSet.add(weekDayHourKey)
    }
    if (view === 'month' && !foundDataSet.has(weekDayKey)) {
      filteredHistoryData.push({ ...historyDataItem, selfProduction })
      selfProduction = 0
      foundDataSet.add(weekDayKey)
    }
    if (view === 'year' && !foundDataSet.has(yearMonthKey)) {
      filteredHistoryData.push({ ...historyDataItem, selfProduction })
      selfProduction = 0
      foundDataSet.add(yearMonthKey)
    }
  }

  const historyChartData: HistoryChartData[] = []
  const factor = view === 'day' ? 4 : 1 // if view = day take value x 4 'cause values are in 15min interval but need to be one hour

  filteredHistoryData.forEach((current, index) => {
    const isYearOrMonthView = view === 'month' || view === 'year'
    const currentToUse = isYearOrMonthView ? filteredHistoryData[index + 1] : current
    const prevToUse = isYearOrMonthView ? current : filteredHistoryData[index - 1]

    if (!currentToUse || !prevToUse) return

    const currentToUseDate = moment(currentToUse.time).toDate()
    const timestampToUse = getHistoryObjectTimestamp(isYearOrMonthView, prevToUse.time, currentToUse.time, view)
    const historyObject = getHistoryObject(
      currentToUse,
      prevToUse,
      factor,
      storageInfo,
      wallboxChartData,
      powermeterChartData,
      systemInfo,
      forecastPower,
      breakthroughData,
      currentToUseDate,
      timestampToUse,
      view,
      dynamicPowerPricesEnabled
    )

    historyChartData.push(historyObject)
  })

  return historyChartData
}

export const getForecastChartDataForView = (
  forecastData: ForecastData[] | undefined,
  forecastPower: ForecastPower[],
  wallboxChartData: Record<number, WallboxHistory[]>,
  powermeterChartData: Record<number, PowermeterHistory[]>,
  breakthroughData: Breakthrough[],
  systemInfo: SystemInfo,
  view?: ChartView
): ForecastChartData[] => {
  if (!forecastData || !forecastData.length || view === 'month' || view === 'year' || view === 'hour' || view === 'week') return []
  const storageInfo = systemInfo?.storage.info
  const dataToConvert = forecastData.filter((data, index) => {
    const timeAsMoment = moment(data.time)
    if (index === forecastData.length - 1) {
      //always keep the last item because we need something to subtract the last needed value from
      return true
    }
    if (view === 'day') {
      return true
    }
    if (view === 'week') {
      return isStartOfHour(timeAsMoment)
    }
    if (view === 'month') {
      return isStartOfDay(timeAsMoment)
    }
    if (view === 'year') {
      return isStartOfMonth(timeAsMoment)
    }

    return false
  })

  const forecastChartData: ForecastChartData[] = []
  const factor = view === 'day' ? 4 : 1 // if view = day take value x 4 'cause values are in 15min interval but need to be one hour

  dataToConvert.forEach((d, index) => {
    const previousValue = dataToConvert[index]
    const currentValue = dataToConvert[index + 1]
    if (index + 1 === dataToConvert.length) {
      return
    }
    const batPowerIn = Math.max(0, (currentValue?.batteryPowerIn - previousValue.batteryPowerIn) * factor)
    const batPowerOut = Math.max(0, (currentValue?.batteryPowerOut - previousValue.batteryPowerOut) * factor)
    const netIn = Math.max(0, (currentValue?.netIn - previousValue.netIn) * factor)
    const consumptionTotal = Math.max(0, (currentValue.consumption - previousValue.consumption) * factor)
    const deration = Math.max(0, (currentValue.consumption - previousValue.consumption) * factor + storageInfo.derateAtPower)
    const netOut = Math.max(0, (currentValue.netOut - previousValue.netOut) * factor)

    const weatherBreakthrough = !!binarySearchObject(breakthroughData, previousValue.time, 'deratePowerAt')

    const forecastDate = new Date(currentValue.time)
    const forecastPowerValue = binarySearchObject(forecastPower, forecastDate.valueOf(), 'time')?.power ?? 0
    /** solar value should be the same as forecast power value */
    const powerSolarValue = forecastPowerValue

    const forecastObject: ForecastChartData = {
      'f-ASOC': currentValue?.asoc,
      'f-BatPowerIn': batPowerIn,
      'f-BatPowerOut': batPowerOut,
      'f-ConsumptionTotal': consumptionTotal,
      'f-DC_PowerSolar': powerSolarValue,
      'f-Deration': deration, //Hausverbrauch + derateAtPower
      ForecastPower: forecastPowerValue,
      'f-NetIn': netIn,
      'f-NetOut': netOut,
      ts: currentValue.time,
      WeatherBreakthrough: weatherBreakthrough,
      forecast: true,
    }

    if (wallboxChartData) {
      Object.entries(wallboxChartData).forEach(([id]) => {
        forecastObject[`f-wb${id}/Wallbox_energyAll`] = ((currentValue[`wb${id}/Wallbox_energyAll`] as number) ?? 0) * factor
        forecastObject[`f-wb${id}/Wallbox_energySolar`] = ((currentValue[`wb${id}/Wallbox_energySolar`] as number) ?? 0) * factor
        forecastObject[`f-wb${id}/Wallbox_energyNetOut`] = ((currentValue[`wb${id}/Wallbox_energyNetOut`] as number) ?? 0) * factor
        forecastObject[`f-wb${id}/Wallbox_energyDischarged`] = ((currentValue[`wb${id}/Wallbox_energyDischarged`] as number) ?? 0) * factor
      })

      forecastObject['f-Wallbox_energySolar'] = (currentValue.Wallbox_energySolar ?? 0) * factor
      forecastObject['f-Wallbox_energyAll'] = (currentValue.Wallbox_energyAll ?? 0) * factor
      forecastObject['f-Wallbox_energyDischarged'] = (currentValue.Wallbox_energyDischarged ?? 0) * factor
    }

    if (powermeterChartData) {
      Object.entries(powermeterChartData).forEach(([id]) => {
        forecastObject[`f-pm${id}/Powermeter_production`] = ((currentValue[`pm${id}/Powermeter_production`] as number) ?? 0) * factor
        forecastObject[`f-pm${id}/Powermeter_consumption`] = ((currentValue[`pm${id}/Powermeter_consumption`] as number) ?? 0) * factor
      })

      forecastObject['f-Powermeter_consumption'] = ((currentValue['Powermeter_consumption'] as number) ?? 0) * factor
      /** removed for CPE-2043 to make the forecast values same as in PortalV2 */
      // forecastObject['f-Powermeter_production'] = ((currentValue['Powermeter_production'] as number) ?? 0) * factor
    }

    forecastObject['f-ConsumptionHouse'] = Math.max(
      0,
      (forecastObject['f-ConsumptionTotal'] as number) -
        (forecastObject['f-Powermeter_consumption'] as number) -
        (forecastObject['f-Wallbox_energyAll'] as number)
    )

    /** removed for CPE-2043 to make the forecast values same as in PortalV2 */
    // forecastObject['f-DC_PowerTotal'] = Math.max(
    //   (forecastObject['f-Powermeter_production'] as number) + (forecastObject['f-DC_PowerSolar'] as number)
    // )

    // forecastObject['f-GreenConsumption'] = Math.max(0, forecastObject['f-DC_PowerTotal'] - batPowerIn - netIn)

    forecastChartData.push(forecastObject)
  })

  return forecastChartData
}

export const fillTimeValues = ({ from, to, view }: { from: Moment; to: Moment; view?: ChartView }): { ts: number }[] => {
  const currentTS = moment(from)
  const timestampEntries: { ts: number }[] = []
  const { stepSize, stepUnit } = getStepSizeAndUnit({ view })

  if (view === 'year' || view === 'month' || view === 'week') {
    currentTS.startOf(stepUnit)
  }

  while (currentTS.isBefore(to)) {
    timestampEntries.push({ ts: currentTS.valueOf() })
    currentTS.add(stepSize, stepUnit)
  }

  return timestampEntries
}

/**
 * Get the x-axis tick's
 * @param param0 the start and end date as well as the view (day, month, ...)
 * @returns the ticks for a graph (shown in the x-axis)
 */
export const getXTickValues = ({ from, to, view, upSm = false }: { from: Moment; to: Moment; view?: ChartView; upSm?: boolean }): number[] => {
  const currentTS = moment(from)
  const tickValues: number[] = []

  const { stepSize, stepUnit } = getStepSizeAndUnitForTicks({ view, upSm })

  if (view === 'year' || view === 'month' || view === 'week') {
    currentTS.startOf(stepUnit)
  }

  while (currentTS.isBefore(to)) {
    tickValues.push(currentTS.valueOf())
    currentTS.add(stepSize, stepUnit)
  }

  return tickValues
}

/**
 * Get the step size and unit for different views (day, month, ...)
 * The step size determines how many ticks are shown in a graph (1 = all, 2 = every second, ...)
 * The step unit determines the tick type (e.g. each tick represents a day like 13.04, ...)
 */
const getStepSizeAndUnitForTicks = ({
  view,
  upSm,
}: {
  view?: ChartView
  upSm?: boolean
}): {
  stepSize: number
  stepUnit: moment.unitOfTime.DurationConstructor
} => {
  const stepSizes = {
    day: upSm ? 2 : 3,
    week: 1,
    month: upSm ? 3 : 4,
    year: upSm ? 1 : 2,
  }

  switch (view) {
    case 'year':
      return { stepSize: stepSizes.year, stepUnit: 'month' }
    case 'month':
      return { stepSize: stepSizes.month, stepUnit: 'day' }
    case 'week':
      return { stepSize: stepSizes.week, stepUnit: 'day' }
    case 'day':
    default:
      return { stepSize: stepSizes.day, stepUnit: 'hours' }
  }
}

const getStepSizeAndUnit = ({
  view,
  forTicks,
}: {
  view?: ChartView
  forTicks?: boolean
}): {
  stepSize: number
  stepUnit: moment.unitOfTime.DurationConstructor
} => {
  switch (view) {
    case 'year':
      return {
        stepSize: 1,
        stepUnit: 'month',
      }
    case 'month':
      return {
        stepSize: 1,
        stepUnit: 'day',
      }
    case 'week':
      return {
        stepSize: 1,
        stepUnit: 'hour',
      }
    case 'day':
      if (forTicks) {
        return {
          stepSize: 1,
          stepUnit: 'hours',
        }
      }
      return {
        stepSize: 15,
        stepUnit: 'minutes',
      }
    default:
      return {
        stepSize: 15,
        stepUnit: 'minutes',
      }
  }
}

export const getCurrentDifference = (currValue: number, prevValue: number | undefined): number => {
  if (!prevValue) {
    return currValue
  }

  return currValue - prevValue
}

export const applySignsToData = (rechartData: RechartData, customTraces?: CustomTrace[]): RechartData => {
  const newRechartData: RechartData = { ...rechartData }

  customTraces?.forEach(customTrace => {
    if (customTrace.sign === -1) {
      newRechartData.data = newRechartData.data?.map(dataObject => {
        const newDataObj = { ...dataObject }
        const oldValue = newDataObj[customTrace.alternateKey ?? customTrace.key]
        newDataObj[customTrace.alternateKey ?? customTrace.key] = -oldValue
        return newDataObj
      })
    }
  })

  return newRechartData
}

const calculateEnergyDischarged = (currentEnergy: number, previousEnergy: number, factor: number): number => {
  if (currentEnergy && previousEnergy) return getRoundedNumber(Math.max(getCurrentDifference(currentEnergy, previousEnergy) * factor, 0))
  return 0
}

export function getHistoryObject(
  current: HistoryData,
  previous: HistoryData,
  factor: number,
  storageInfo: StorageInfoData,
  wallboxChartData: Record<number, WallboxHistory[]>,
  powermeterChartData: Record<string, PowermeterHistory[]>,
  systemInfo: SystemInfo,
  forecastPower: ForecastPower[],
  breakthroughData: Breakthrough[],
  currentToUseDate: Date,
  timestampToUse: number,
  view: ChartView,
  dynamicPowerPricesEnabled: boolean /* this is a temporary condition added with AF-3237. The plan is to remove it after SW1 and SW2 align. */
): HistoryChartData {
  const netIn = calculateDifference(current.NetIn, previous.NetIn)
  const batPowerIn = calculateDifference(current?.BatPowerIn, previous?.BatPowerIn)
  const batPowerOut = calculateDifference(current?.BatPowerOut, previous?.BatPowerOut)
  const powerSolar = calculateDifference(
    current.DC_PowerL1 + current.DC_PowerL2 + current.DC_PowerL3,
    previous.DC_PowerL1 + previous.DC_PowerL2 + previous.DC_PowerL3
  )
  const deration = calculatePowerDifference(current.Consumption, previous.Consumption)
  const netOut = calculateDifference(current.NetOut, previous.NetOut)
  const consumptionTotal = calculateDifference(current.Consumption, previous.Consumption)
  const powerSolarL1 = calculateDifference(current.DC_PowerL1, previous.DC_PowerL1)
  const powerSolarL2 = calculateDifference(current.DC_PowerL2, previous.DC_PowerL2)
  const powerSolarL3 = calculateDifference(current.DC_PowerL3, previous.DC_PowerL3)
  /**
   * Self production is currently calculated in two ways based on if dynamic power prices are enabled:
   * (enabled) the difference is calculated beforehand. This is done to remove negative values from the calculation and is more precise.
   * (disabled) If dynamic power prices are deactivated the difference is calculated here. Its less precise but returns the same results als portal V2.
   */
  const selfProduction = dynamicPowerPricesEnabled
    ? current.selfProduction
    : getRoundedNumber(Math.max(0, getCurrentDifference(consumptionTotal, netOut)))

  const wallboxes = getWallboxes(wallboxChartData, current.time, previous.time)
  const powermeters = getPowermeters(powermeterChartData, systemInfo, current.time, previous.time)

  const historyObject: Partial<HistoryChartData> = {
    BatPowerIn: batPowerIn,
    BatPowerOut: batPowerOut,
    DC_PowerL1: powerSolarL1,
    DC_PowerL2: powerSolarL2,
    DC_PowerL3: powerSolarL3,
    DC_PowerSolar: powerSolar,
    Deration: deration,
    NetIn: netIn,
    NetOut: netOut,
    ConsumptionTotal: consumptionTotal, // * consumptionSign,
    ConsumptionHouse: consumptionTotal, // * consumptionSign,
    SelfProduction: selfProduction,
    Autarky: selfProduction,
  }

  if (wallboxes.length > 0) {
    let energySolarSum = 0
    let energyAllSum = 0
    let energyDischargedSum = 0

    wallboxes.forEach(({ id, currentWB, previousWB }) => {
      const firstWallboxTimestamp = systemInfo.wallboxes.wallboxes.find(wallbox => `${wallbox.number}` === id)?.info.first

      /** Need to add 1 hour, because the wallboxes = getWallboxes() function adds 1 hour */
      if (firstWallboxTimestamp && current.time < firstWallboxTimestamp + ONE_HOUR_S) {
        return
      }

      if (currentWB && previousWB) {
        const energyAll = getRoundedNumber(getCurrentDifference(currentWB.energyAll, previousWB.energyAll) * factor)
        const energySolar = getRoundedNumber(getCurrentDifference(currentWB.energySolar, previousWB.energySolar) * factor)
        const energyDischarged = calculateEnergyDischarged(currentWB.energyDischarged, previousWB.energyDischarged, factor)

        historyObject[`wb${id}/Wallbox_energyAll`] = energyAll
        historyObject[`wb${id}/Wallbox_energySolar`] = energySolar
        historyObject[`wb${id}/Wallbox_energyNetOut`] = energyAll - energySolar
        historyObject[`wb${id}/Wallbox_energyDischarged`] = energyDischarged

        energyAllSum += energyAll
        energySolarSum += energySolar
        energyDischargedSum += energyDischarged
      }
    })

    historyObject['Wallbox_energySolar'] = energySolarSum
    historyObject['Wallbox_energyAll'] = energyAllSum
    historyObject['Wallbox_energyDischarged'] = energyDischargedSum
  }

  if (powermeters.length > 0) {
    let productionSum = 0
    let consumptionSum = 0
    let bypassSum = 0
    let bypassNoChargeSum = 0

    powermeters.forEach(({ id, type, prevPowermeter, currentPowermeter }) => {
      if (currentPowermeter && prevPowermeter) {
        const deltaNetIn = getRoundedNumber(getCurrentDifference(currentPowermeter.NetIn, prevPowermeter.NetIn) * factor)
        const deltaNetOut = getRoundedNumber(getCurrentDifference(currentPowermeter.NetOut, prevPowermeter.NetOut) * factor)

        if (isProduction(type)) {
          historyObject[`pm${id}/Powermeter_production`] = Math.max(0, deltaNetIn)
          productionSum += Math.max(0, deltaNetIn)
        }

        if (isConsumption(type)) {
          historyObject[`pm${id}/Powermeter_consumption`] = deltaNetOut
          consumptionSum += deltaNetOut
        }

        if (isBypass(type)) {
          historyObject[`pm${id}/Powermeter_NetIn_bypass`] = Math.max(0, deltaNetIn)
          bypassSum += Math.max(0, deltaNetIn)
        }

        if (isBypassNoCharge(type)) {
          historyObject[`pm${id}/Powermeter_NetIn_bypass_noCharge`] = Math.max(0, deltaNetIn)
          bypassNoChargeSum += Math.max(0, deltaNetIn)
        }

        if (isEnera(type)) {
          historyObject[`pm${id}/Powermeter_NetIn_enera`] = Math.max(0, deltaNetIn)
        }
      }
    })

    historyObject['Powermeter_NetIn_bypass_noCharge'] = Math.max(0, getRoundedNumber(bypassNoChargeSum))
    historyObject['Powermeter_NetIn_bypass'] = Math.max(0, getRoundedNumber(bypassSum))
    historyObject['Powermeter_consumption'] = Math.max(0, getRoundedNumber(consumptionSum))
    historyObject['Powermeter_production'] = Math.max(0, getRoundedNumber(productionSum))
  }

  historyObject['ConsumptionHouse'] = Math.max(
    0,
    getRoundedNumber(consumptionTotal - (historyObject['Powermeter_consumption'] ?? 0) - (historyObject['Wallbox_energyAll'] ?? 0))
  )

  historyObject['DC_PowerTotal'] = getRoundedNumber((historyObject['Powermeter_production'] ?? 0) + (historyObject['DC_PowerSolar'] ?? 0))

  historyObject['DirectConsumption'] = getRoundedNumber(
    Math.max(0, (historyObject['DC_PowerTotal'] ?? 0) - (historyObject['BatPowerIn'] ?? 0) - (historyObject['NetIn'] ?? 0))
  )
  historyObject['GreenConsumption'] = Math.max(0, historyObject['DC_PowerTotal'] - batPowerIn - netIn)

  historyObject.DstSwitch = !moment(previous.time).isDST() && moment(current.time).isDST()
  historyObject.ASOC = getRoundedNumber(current.ASOC)
  historyObject.ForecastPower = getRoundedNumber(binarySearchObject(forecastPower, currentToUseDate.valueOf(), 'time')?.power ?? 0)
  /**
   * TODO: Should current.time be replace for WeatherBreakthrough and for ts as well?
   * For weatherBreakthrough I could not see any difference
   * For ts the result affects the forecast power and looks wrong compared to what is displayed in the old portal
   */
  historyObject.WeatherBreakthrough = !!binarySearchObject(breakthroughData, current.time, 'deratePowerAt')
  historyObject.ts = timestampToUse
  historyObject.Temperature = current.Temperature

  function calculateDifference(current: number, previous: number): number {
    return getRoundedNumber(Math.max(0, getCurrentDifference(current, previous) * factor))
  }

  function calculatePowerDifference(current: number, previous: number): number {
    return getRoundedNumber(Math.max(0, getCurrentDifference(current, previous) * factor + storageInfo.derateAtPower))
  }

  function getWallboxes(
    wallboxChartData: Record<number, WallboxHistory[]>,
    currentTime: number,
    previousTime: number
  ): { id: string; currentWB?: WallboxHistory; previousWB?: WallboxHistory }[] {
    const currToMoment = moment(currentTime).add(1, 'hour').valueOf()
    const prevToMoment = moment(previousTime).add(1, 'hour').valueOf()

    return Object.entries(wallboxChartData).map(([id, data]) => {
      const currentWB = binarySearchObject(data, currentTime, 'time') || getMatchingTimestampItemFromArray(data, currToMoment, view)
      const previousWB =
        binarySearchObject(data, previousTime, 'time') ||
        /*
         *binarySearchObject cannot find the very first entry because previousTime hour is 00:00:00 whereas the actual data hour can be different e.g 14:00:00
         */
        getMatchingTimestampItemFromArray(data, prevToMoment, view)

      /** the getMatchingTimestampItemFromArray sometimes return a value, which time is smaller then currentWB.time */
      if ((currentWB?.time ?? 0) < (previousWB?.time ?? 0)) {
        return { id, currentWB, previousWB: undefined }
      }

      return {
        id: id,
        currentWB,
        previousWB,
      }
    })
  }

  function getPowermeters(
    powermeterChartData: Record<string, PowermeterHistory[]>,
    systemInfo: SystemInfo,
    currentTime: number,
    previousTime: number
  ): {
    id: string
    type?: PowermeterType[]
    currentPowermeter?: PowermeterHistory
    prevPowermeter?: PowermeterHistory
  }[] {
    const currToMoment = moment(currentTime).add(1, 'hour').valueOf()
    const prevToMoment = moment(previousTime).add(1, 'hour').valueOf()

    return Object.entries(powermeterChartData).map(([id, data]) => {
      const powermeterInfo = systemInfo.powermeters.powermeters.find(pm => pm.uniqueId === id)

      return {
        id,
        type: powermeterInfo?.type,
        currentPowermeter: binarySearchObject(data, currentTime, 'time') || getMatchingTimestampItemFromArray(data, currToMoment, view),
        prevPowermeter: binarySearchObject(data, previousTime, 'time') || getMatchingTimestampItemFromArray(data, prevToMoment, view),
      }
    })
  }

  return historyObject as HistoryChartData
}

export const getDateTimeLabel = (dateTime: Moment, t: TFunction, view?: ChartView): string => {
  switch (view) {
    case 'day':
    case 'week': {
      /* todo: update when we have updated chart values handling. Currently are the values wrong */
      const longDateFormat = `${t('common.format.date')} HH:mm`
      return `${dateTime.format(longDateFormat)}`
    }
    case 'month':
    case 'year': {
      return `${dateTime.format(t('common.format.date'))}`
    }
    default: {
      return dateTime.format(`${t('common.format.date')} - HH:mm:ss`)
    }
  }
}

export const getMatchingTimestampItemFromArray = <T extends { time: number | Moment } | { ts: number | Moment }>(
  data: T[],
  timestamp: number,
  view: ChartView
): T | undefined => {
  const viewMap: {
    [key in ChartView]: ChartView | 'minute' | 'second'
  } = {
    year: 'month',
    month: 'day',
    week: 'hour',
    day: 'minute',
    hour: 'second',
  }

  const date1 = moment(timestamp).startOf(viewMap[view])

  const matchingTimestampItem = data.find(item => {
    const toMoment = 'time' in item ? moment(item.time) : moment(item.ts)
    const date2 = toMoment.startOf(viewMap[view])

    return date1.isSame(date2)
  })

  return matchingTimestampItem
}
